import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { EnlacePago, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import { Chip, type EstadoChip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { minutosYSegundos, useCuentaAtras } from '../logica/cuentaAtras';
import { dinero } from '../logica/dinero';
import { navegador } from '../logica/navegador';
import { RUTAS_CLIENTE } from '../rutas';
import { AvisoPc, BotonPc, Pagina } from './Pagina';

/**
 * Página de pago (4.9, `/pagar/{business_id}/{link_code}`, el `payment_link_url`): anónima, con `getPaymentLink`.
 *
 * - Pendiente: la plaza guardada con su cuenta atrás hasta `expires_at` (G39; a 5 minutos o menos, «Caduca pronto»), qué
 *   se paga (`description`, G283), el total (`amount` ya incluye los gastos de gestión, G292), las condiciones con
 *   `conditions_text` tal cual (D-004) y «Pagar» o, si es la tarjeta de garantía, «Guardar mi tarjeta» (G291) → la página
 *   segura de Stripe (`checkout_url`). Tarjeta, Apple Pay y Google Pay (G95); los datos de la tarjeta nunca se ven (G28).
 * - Stripe vuelve con `?vuelta=1`: se vuelve a leer el estado (procesando → pagado, mirándolo cada pocos segundos).
 * - Vuelta sin pagar: «Volver a intentarlo» y, debajo, «Volver a [negocio] sin pagar» a `return_url` (G938) o, sin
 *   ella, a la web publicada del negocio (`business.site_url`, G982); sin las dos, el enlace no sale.
 * - Estados de G291: pagado, tarjeta guardada, esperando al banco, rechazado, caducado, plaza liberada y devuelto (pago
 *   tardío sin plaza); y «No encontramos este enlace de pago» (404).
 * La cabecera y la privacidad del negocio saldrán de la web pública (`getPublicSite`), que aún no existe y pide el slug:
 * mientras, la página no dice el negocio y enlaza solo la privacidad de Reservaria.
 */
interface Props {
    ops: OperacionesClienteFinal;
    negocio: string;
    codigo: string;
    vuelta: boolean;
    navegar: (ruta: string) => void;
    ahora?: () => number;
    /** Cada cuánto se vuelve a mirar un pago «en proceso» (las pruebas lo acortan). */
    espera?: number;
}

type Carga = { tipo: 'cargando' } | { tipo: 'error' } | { tipo: 'no-valido' } | { tipo: 'listo'; enlace: EnlacePago };

const CINCO_MINUTOS = 300;

export function PaginaPago({ ops, negocio, codigo, vuelta, navegar, ahora = Date.now, espera = 4000 }: Props) {
    const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const [yendo, setYendo] = useState(false);

    const leer = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let vivo = true;
        ops.getPaymentLink(negocio, codigo)
            .then((enlace) => {
                if (vivo) {
                    setCarga({ tipo: 'listo', enlace });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCarga(e instanceof ErrorApi && e.estado === 404 ? { tipo: 'no-valido' } : { tipo: 'error' });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, codigo, intento]);

    // «Esperando a tu banco»: se vuelve a mirar sola.
    const procesando = carga.tipo === 'listo' && carga.enlace.status === 'processing';
    useEffect(() => {
        if (!procesando) {
            return;
        }
        const reloj = setTimeout(leer, espera);
        return () => {
            clearTimeout(reloj);
        };
    }, [procesando, intento, leer, espera]);

    const enlace = carga.tipo === 'listo' ? carga.enlace : null;
    const queda = useCuentaAtras(enlace?.status === 'pending' ? enlace.expires_at : null, ahora);
    // Al llegar a cero, se vuelve a leer: la plaza se ha liberado.
    useEffect(() => {
        if (queda !== 0) {
            return;
        }
        const reloj = setTimeout(leer, 0);
        return () => {
            clearTimeout(reloj);
        };
    }, [queda, leer]);

    const garantia = enlace?.concept === 'guarantee';
    const marco = (hijos: ReactNode) => (
        <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'reservaria' }} centrada={enlace === null || enlace.status !== 'pending'}>
            {hijos}
        </Pagina>
    );

    if (carga.tipo === 'cargando') {
        return marco(
            <section className="rv-pc-card" aria-busy="true" aria-label="Cargando el pago">
                <div className="rv-pc-esqueleto" style={{ height: 28, width: '60%' }} />
                <div className="rv-pc-esqueleto" style={{ height: 48, width: '40%' }} />
                <div className="rv-pc-esqueleto" style={{ height: 80 }} />
            </section>,
        );
    }
    if (carga.tipo === 'error') {
        return marco(
            <section className="rv-pc-card">
                <div className="rv-pc-card__cab"><h1>No hemos podido cargar el pago</h1><span>Comprueba la conexión y prueba de nuevo. No se ha cobrado nada.</span></div>
                <BotonPc texto="Reintentar" icono="refresh-cw" onClick={leer} />
            </section>,
        );
    }
    if (carga.tipo === 'no-valido') {
        return marco(<Resultado icono="link" fondo="var(--rv-paper)" color="var(--rv-ink)" chip={['Enlace no válido', 'problem']} titulo="No encontramos este enlace de pago" texto="Puede que esté incompleto. Copia el enlace entero del mensaje o pide uno nuevo al negocio." />);
    }

    const e = carga.enlace;
    const volver = e.return_url == null ? null : { texto: 'Volver', icono: 'arrow-right' as const, accion: () => { navegador.ir(e.return_url ?? ''); } };
    const misPagos = { texto: 'Ver mis pagos', icono: 'receipt' as const, accion: () => { navegar(RUTAS_CLIENTE.misPagos); } };
    const filas: [string, string][] = [
        ...(e.description === undefined || e.description === '' ? [] : [[garantia ? 'Garantía de tu reserva' : 'Qué pagas', e.description] as [string, string]]),
        [garantia ? 'Cobrado hoy' : 'Importe', dinero(e.amount)],
    ];

    switch (e.status) {
        case 'completed':
            return marco(<Resultado icono="check" fondo="var(--rv-red)" color="#fff" chip={['Pagado', 'confirmed']} titulo="Ya está pagado" texto={`Hemos recibido ${dinero(e.amount)}. Te llegará la confirmación en unos minutos.`} filas={filas} botones={[...(volver === null ? [] : [volver]), misPagos]} nota={['receipt', 'El recibo lo tienes en «Mis pagos».']} />);
        case 'guarantee_saved':
            return marco(<Resultado icono="check" fondo="var(--rv-red)" color="#fff" chip={['Tarjeta guardada', 'confirmed']} titulo="Tarjeta guardada" texto="No te hemos cobrado nada. Te llegará la confirmación en unos minutos." filas={filas} botones={volver === null ? [misPagos] : [volver, misPagos]} />);
        case 'processing':
            return marco(
                <Resultado icono="loader-circle" girando fondo="var(--rv-paper)" color="var(--rv-ink)" chip={['En proceso', 'pending']} titulo="Esperando a tu banco" texto="Tu banco todavía no ha confirmado el pago. No vuelvas a pagar: esta página se actualiza sola en cuanto lo confirme." filas={filas} botones={[{ texto: 'Comprobar ahora', icono: 'refresh-cw', accion: leer, secundario: true }]} />,
            );
        case 'rejected':
            return marco(<Resultado icono="x" fondo="var(--rv-red-soft)" color="var(--rv-red)" chip={['Rechazado', 'problem']} titulo="No se ha podido cobrar" texto="No te hemos cobrado nada y la plaza ya no está guardada. Si todavía quieres, vuelve a empezar desde la web del negocio." filas={filas} botones={volver === null ? [] : [volver]} />);
        case 'expired':
            return marco(<Resultado icono="link" fondo="var(--rv-paper)" color="var(--rv-ink)" chip={['Caducado', 'problem']} titulo="Este enlace de pago ha caducado" texto="Los enlaces de pago duran 30 minutos. No te hemos cobrado nada. Si todavía quieres, empieza de nuevo o pide un enlace nuevo al negocio." botones={volver === null ? [] : [volver]} />);
        case 'released':
            return marco(<Resultado icono="hourglass" fondo="var(--rv-paper)" color="var(--rv-ink)" chip={['Caducado', 'problem']} titulo="Se ha liberado la plaza" texto={`Han pasado los 30 minutos sin ${garantia ? 'guardar la tarjeta' : 'pagar'}. No te hemos cobrado nada. Si todavía hay hueco, puedes volver a reservar.`} botones={volver === null ? [] : [volver]} />);
        case 'refunded':
            return marco(
                <Resultado icono="undo-2" fondo="var(--rv-ink)" color="#fff" chip={['Devuelto', 'confirmed']} titulo="Te devolvemos el dinero" texto={`Tu pago llegó cuando la plaza ya se había liberado y no quedaba hueco. Te hemos devuelto ${dinero(e.amount)}: puede tardar unos días en verse en tu cuenta.`} filas={filas} botones={volver === null ? [misPagos] : [misPagos, { ...volver, secundario: true }]} />,
            );
        case 'pending':
            break;
    }

    // Pendiente: la página para pagar.
    const pronto = queda !== null && queda <= CINCO_MINUTOS;
    // G982: sin `return_url`, la web publicada del negocio; sin las dos, no hay a dónde volver.
    const salida = e.return_url ?? e.business.site_url ?? null;
    const vencimiento = new Date(e.expires_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const ir = () => {
        if (e.checkout_url == null) {
            return;
        }
        setYendo(true);
        navegador.ir(e.checkout_url);
    };
    return marco(
        <>
            {vuelta && <AvisoPc tipo="espera" texto="No se ha completado el pago. Puedes volver a intentarlo mientras la plaza siga guardada." />}
            <section className="rv-pc-card rv-pc-plazo" aria-label="Plaza guardada">
                <div className="rv-pc-plazo__fila">
                    <span className={`rv-pc-plazo__baldosa${pronto ? ' rv-pc-plazo__baldosa--pronto' : ''}`}><Icono nombre="hourglass" /></span>
                    <span className="rv-pc-plazo__texto">
                        <strong>Te guardamos la plaza</strong>
                        <span>{`Hasta las ${vencimiento}`}</span>
                    </span>
                    <strong className={`rv-pc-plazo__reloj${pronto ? ' rv-pc-plazo__reloj--pronto' : ''}`} aria-label={queda === null ? undefined : `Quedan ${String(Math.floor(queda / 60))} minutos y ${String(queda % 60)} segundos`}>
                        {queda === null ? '' : minutosYSegundos(queda)}
                    </strong>
                </div>
                <span className="rv-pc-plazo__barra" aria-hidden="true"><span style={{ width: `${String(queda === null ? 0 : Math.min(100, (queda / 1800) * 100))}%` }} className={pronto ? 'rv-pc-plazo__barra--pronto' : undefined} /></span>
                <div className="rv-pc-plazo__pie">
                    <span>{garantia ? 'Si no la guardas antes, la reserva se anula y no se cobra nada.' : 'Si no pagas antes, vuelve a quedar libre y no se cobra nada.'}</span>
                    <Chip estado={pronto ? 'pending' : 'neutral'}>{pronto ? 'Caduca pronto' : 'Guardada'}</Chip>
                </div>
            </section>
            <section className="rv-pc-card">
                <div className="rv-pc-card__cab">
                    <span className="rv-pc-ceja">{garantia ? 'Tarjeta de garantía' : 'Tu pago'}</span>
                    {e.description !== undefined && e.description !== '' && <h1>{e.description}</h1>}
                </div>
                <div className="rv-pc-total">
                    <span>{garantia ? 'Hoy pagas' : 'Total a pagar'}</span>
                    <strong>{dinero(e.amount)}</strong>
                    {e.booking_fee !== undefined && e.booking_fee.amount > 0 && <small>{`Incluye ${dinero(e.booking_fee)} de gastos de gestión`}</small>}
                    {garantia && <small>Solo guardamos tu tarjeta como garantía.</small>}
                </div>
                <p className="rv-pc-nota">
                    <span>{'Pagas a '}<b>{e.business.name}</b></span>
                    <a href={e.business.privacy_url} target="_blank" rel="noreferrer">Su privacidad</a>
                </p>
                {e.conditions_text != null && e.conditions_text !== '' && (
                    <div className="rv-pc-caja">
                        <strong className="rv-pc-caja__titulo"><Icono nombre="scale" />{garantia ? 'Cuándo se cobraría' : 'Si cancelas'}</strong>
                        <span>{e.conditions_text}</span>
                    </div>
                )}
            </section>
            <BotonPc texto={vuelta ? 'Volver a intentarlo' : garantia ? 'Guardar mi tarjeta' : `Pagar ${dinero(e.amount)}`} icono={vuelta ? 'rotate-ccw' : 'lock'} ocupado={yendo} ocupadoTexto="Abriendo la página de pago…" desactivado={e.checkout_url == null} onClick={ir} />
            {/* G938: el regreso sin pagar lo resuelve esta página (también para las entradas de 10.13); lo guardado se libera solo al caducar. */}
            {vuelta && salida !== null && (
                <button type="button" className="rv-pc-l rv-pc-l--centro rv-pc-l--subrayado rv-final-osc" onClick={() => { navegador.ir(salida); }}>
                    {`Volver a ${e.business.name} sin pagar`}
                </button>
            )}
            <div className="rv-pc-metodos" aria-label="Formas de pago">
                {(
                    [
                        ['credit-card', 'Tarjeta'],
                        ['smartphone', 'Apple Pay'],
                        ['smartphone', 'Google Pay'],
                    ] as [NombreIcono, string][]
                ).map(([icono, t]) => (
                    <span key={t} className="rv-pc-metodo"><Icono nombre={icono} />{t}</span>
                ))}
            </div>
            <p className="rv-pc-nota rv-final-osc"><Icono nombre="lock" /><span>El pago se hace en la página segura de Stripe. Nunca vemos ni guardamos los datos de tu tarjeta.</span></p>
        </>,
    );
}

interface Boton {
    texto: string;
    icono: NombreIcono;
    accion: () => void;
    secundario?: boolean;
}

function Resultado({ icono, fondo, color, chip, titulo, texto, filas = [], botones = [], nota, girando = false }: { icono: NombreIcono; fondo: string; color: string; chip: [string, EstadoChip]; titulo: string; texto: string; filas?: [string, string][]; botones?: Boton[]; nota?: [NombreIcono, string]; girando?: boolean }) {
    return (
        <section className="rv-pc-card" aria-labelledby="rv-pc-pago-t">
            <div className="rv-pc-baldosa-fila">
                <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: fondo, color }}>
                    <Icono nombre={icono} className={girando ? 'rv-gira' : undefined} />
                </span>
                <Chip estado={chip[1]}>{chip[0]}</Chip>
            </div>
            <div className="rv-pc-card__cab">
                <h1 id="rv-pc-pago-t">{titulo}</h1>
                <span>{texto}</span>
            </div>
            {filas.length > 0 && (
                <div className="rv-pc-lineas">
                    {filas.map(([k, v]) => (
                        <div key={k} className="rv-pc-dato"><span>{k}</span><strong>{v}</strong></div>
                    ))}
                </div>
            )}
            {botones.length > 0 && (
                <div className="rv-pc-acciones">
                    {botones.map((b, i) => <BotonPc key={b.texto} texto={b.texto} icono={b.icono} tipo={b.secundario === true || i > 0 ? 'sec' : 'pri'} onClick={b.accion} />)}
                </div>
            )}
            {nota !== undefined && <p className="rv-pc-nota"><Icono nombre={nota[0]} /><span>{nota[1]}</span></p>}
        </section>
    );
}
